import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../utils/api.constants';
import {
  DbConfigResponse,
  DbConfigUpsertResponse,
  DbCredentials,
  DbType,
  MdmDbConfigCreateRequest,
  MdmDbConfigUpdateRequest,
  MdmDbCredentials
} from '../models/tenant-service-db.model';

@Injectable({
  providedIn: 'root'
})
export class TenantServiceDbService {

  constructor(private http: HttpClient) { }

  // Combined db_type + credentials endpoint — atomic, recommended for admin-panel flows
  // (keeps the tenant's active db_type and its credentials row from drifting out of sync).
  getDbConfig(tenantUid: string, dbType?: DbType): Observable<DbConfigResponse> {
    let params = new HttpParams();
    if (dbType) {
      params = params.set('db_type', dbType);
    }
    return this.http.get<DbConfigResponse>(API_ENDPOINTS.TENANT_SERVICE_DB.CONFIG(tenantUid), { params });
  }

  saveDbConfig(tenantUid: string, payload: { db_type: DbType } & Record<string, unknown>): Observable<DbConfigUpsertResponse> {
    return this.http.put<DbConfigUpsertResponse>(API_ENDPOINTS.TENANT_SERVICE_DB.CONFIG(tenantUid), payload);
  }

  // Per-type credential CRUD — full method surface for direct control when needed.
  getCredentials(tenantUid: string, dbType: DbType): Observable<DbCredentials> {
    return this.http.get<DbCredentials>(this.perTypeUrl(tenantUid, dbType));
  }

  createCredentials(tenantUid: string, dbType: DbType, payload: Record<string, unknown>): Observable<DbCredentials> {
    return this.http.post<DbCredentials>(this.perTypeUrl(tenantUid, dbType), payload);
  }

  updateCredentials(tenantUid: string, dbType: DbType, payload: Record<string, unknown>): Observable<DbCredentials> {
    return this.http.put<DbCredentials>(this.perTypeUrl(tenantUid, dbType), payload);
  }

  patchCredentials(tenantUid: string, dbType: DbType, payload: Record<string, unknown>): Observable<DbCredentials> {
    return this.http.patch<DbCredentials>(this.perTypeUrl(tenantUid, dbType), payload);
  }

  deleteCredentials(tenantUid: string, dbType: DbType): Observable<void> {
    return this.http.delete<void>(this.perTypeUrl(tenantUid, dbType));
  }

  getMdmDbConfig(tenantUid: string): Observable<MdmDbCredentials> {
    return this.http.get<MdmDbCredentials>(API_ENDPOINTS.TENANT_SERVICE_DB.MDM.DETAIL(tenantUid));
  }

  createMdmDbConfig(payload: MdmDbConfigCreateRequest): Observable<MdmDbCredentials> {
    return this.http.post<MdmDbCredentials>(API_ENDPOINTS.TENANT_SERVICE_DB.MDM.LIST, payload);
  }

  updateMdmDbConfig(tenantUid: string, payload: MdmDbConfigUpdateRequest): Observable<MdmDbCredentials> {
    return this.http.patch<MdmDbCredentials>(API_ENDPOINTS.TENANT_SERVICE_DB.MDM.DETAIL(tenantUid), payload);
  }

  deleteMdmDbConfig(tenantUid: string): Observable<void> {
    return this.http.delete<void>(API_ENDPOINTS.TENANT_SERVICE_DB.MDM.DETAIL(tenantUid));
  }

  private perTypeUrl(tenantUid: string, dbType: DbType): string {
    switch (dbType) {
      case 'MSSQL':
        return API_ENDPOINTS.TENANT_SERVICE_DB.MSSQL(tenantUid);
      case 'SNOWFLAKE':
        return API_ENDPOINTS.TENANT_SERVICE_DB.SNOWFLAKE(tenantUid);
      case 'DATABRICKS':
        return API_ENDPOINTS.TENANT_SERVICE_DB.DATABRICKS(tenantUid);
    }
  }
}
